import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, onAuthStateChanged } from 'firebase/auth';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';

// Firebase configuration
// Replace these with your actual Firebase project configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Initialize Analytics with GDPR compliance
let analytics = null;

// Only initialize analytics in browser environment and not during SSR
if (typeof window !== 'undefined') {
  // Check for existing consent
  const savedConsent = localStorage.getItem('cookie-consent');
  let analyticsConsent = false;
  
  if (savedConsent) {
    try {
      const parsedConsent = JSON.parse(savedConsent);
      analyticsConsent = parsedConsent.analytics === true;
    } catch (e) {
      console.error('Error parsing consent data:', e);
    }
  }

  // Initialize analytics with appropriate consent settings
  analytics = getAnalytics(app);
  
  // Set default consent to 'denied' until explicitly granted
  if (!analyticsConsent) {
    window.gtag = window.gtag || function() {
      (window.dataLayer = window.dataLayer || []).push(arguments);
    };
    
    window.gtag('consent', 'default', {
      'analytics_storage': 'denied'
    });
  }
}

// Auth functions
export const registerUser = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error) {
    return { user: null, error };
  }
};

export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error) {
    return { user: null, error };
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error };
  }
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error };
  }
};

export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });
};

// Analytics functions
export const trackEvent = (eventName, eventParams = {}) => {
  if (analytics) {
    logEvent(analytics, eventName, eventParams);
  }
};

export const setAnalyticsUser = (userId) => {
  if (analytics && userId) {
    setUserId(analytics, userId);
  }
};

export const setAnalyticsUserProperties = (properties) => {
  if (analytics && properties) {
    setUserProperties(analytics, properties);
  }
};

export { auth, analytics };
export default app;
